import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Users, ShieldOff, Search, ArrowLeft, Tag, GitMerge, ChevronUp, ChevronDown, UserX, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import UserDetailModal from '@/components/admin/UserDetailModal';
import CustomerFollowUpPanel from '@/components/admin/CustomerFollowUpPanel';
import MergeAccountsModal from '@/components/admin/MergeAccountsModal';
import CustomerDirectoryRow from '@/components/admin/CustomerDirectoryRow';
import CustomerTagManager from '@/components/admin/crm/CustomerTagManager';
import { getTierForPoints, LOYALTY_TIERS } from '@/lib/loyaltyTiers';

const PAID_STATUSES = ['paid', 'completed', 'dispatched', 'delivered', 'in_production', 'packed'];

const SORT_COLS = [
  { key: 'name', label: 'NAME', span: 'sm:col-span-3' },
  { key: 'email', label: 'EMAIL', span: 'sm:col-span-3', nosort: true },
  { key: 'tier', label: 'TIER', span: 'sm:col-span-2' },
  { key: 'spend', label: 'SPEND', span: 'sm:col-span-2' },
  { key: 'points', label: 'POINTS', span: 'sm:col-span-1' },
  { key: 'joined', label: 'JOINED', span: 'sm:col-span-1' },
];

// Normalisation helpers for duplicate detection.
const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
const normPostcode = (s) => (s || '').replace(/\s+/g, '').toLowerCase();
// Gmail-style: dots and +aliases are ignored, so john.doe+x@gmail.com == johndoe@gmail.com.
const emailLocalPart = (em) => {
  const [local] = (em || '').toLowerCase().split('@');
  if (!local) return '';
  return local.replace(/\+.*$/, '').replace(/\./g, '');
};

// Returns the match signal(s) shared by every member of a group.
function groupReason(g) {
  const names = new Set(g.map((u) => normName([u.first_name, u.last_name].filter(Boolean).join(' '))));
  const pcs = new Set(g.map((u) => normPostcode(u.delivery_postcode)).filter(Boolean));
  const els = new Set(g.map((u) => emailLocalPart(u.email)).filter(Boolean));
  const reasons = [];
  if (names.size === 1 && [...names][0].length >= 4) reasons.push('NAME');
  if (pcs.size === 1) reasons.push('POSTCODE');
  if (els.size === 1) reasons.push('EMAIL');
  return reasons.join(' + ') || 'MATCH';
}

// Stable key for an unordered pair of user IDs, used to remember dismissed
// duplicate suggestions so two genuinely-separate customers (e.g. two people
// at the same address) stop being flagged.
const pairKey = (idA, idB) => { const [a, b] = [idA, idB].sort(); return `${a}|${b}`; };
const groupPairKeys = (g) => {
  const keys = [];
  for (let i = 0; i < g.length; i++) for (let j = i + 1; j < g.length; j++) keys.push(pairKey(g[i].id, g[j].id));
  return keys;
};

export default function AdminCustomers({ embedded = false }) {
  const { user: currentUser, isLoadingAuth } = useAuth();
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState(''); // '' | 'has_orders' | 'no_orders'
  const [sort, setSort] = useState({ field: 'joined', dir: 'desc' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showMerge, setShowMerge] = useState(false);
  const [mergePrefill, setMergePrefill] = useState(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [ignoring, setIgnoring] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [dupTab, setDupTab] = useState('duplicates'); // 'duplicates' | 'dismissed'

  const { data: tags = [] } = useQuery({
    queryKey: ['customer-tags'],
    queryFn: () => base44.entities.CustomerTag.list('sort_order', 200),
    enabled: currentUser?.role === 'admin',
  });

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const res = await base44.functions.invoke('adminListUsers');
      return res.data?.users || [];
    },
    enabled: currentUser?.role === 'admin',
  });

  // Aggregate loyalty + order spend per customer email in bulk so the
  // directory can show tier / spend / points without a per-row fetch.
  const { data: ledger = [] } = useQuery({
    queryKey: ['admin-loyalty-ledger-all'],
    queryFn: () => base44.entities.LoyaltyLedger.list('-created_date', 500),
    enabled: currentUser?.role === 'admin',
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['admin-orders-for-stats'],
    queryFn: () => base44.entities.Order.list('-created_date', 500),
    enabled: currentUser?.role === 'admin',
  });

  // Admin-dismissed duplicate pairs (kept separate on purpose).
  const { data: dismissals = [] } = useQuery({
    queryKey: ['duplicate-dismissals'],
    queryFn: () => base44.entities.DuplicateDismissal.list('-created_date', 500),
    enabled: currentUser?.role === 'admin',
  });
  const dismissedKeys = useMemo(() => new Set((dismissals || []).map((d) => d.pair_key)), [dismissals]);

  const statsByEmail = useMemo(() => {
    const map = {};
    const ensure = (e) => (map[e] = map[e] || { lifetimeEarned: 0, pointsBalance: 0, orderCount: 0, totalSpend: 0 });
    (ledger || []).forEach((r) => {
      if (!r.customer_email) return;
      const p = Number(r.points) || 0;
      const s = ensure(r.customer_email);
      if (p > 0) s.lifetimeEarned += p;
      s.pointsBalance += p;
    });
    (orders || []).forEach((o) => {
      if (!o.customer_email) return;
      const s = ensure(o.customer_email);
      s.orderCount += 1;
      if (PAID_STATUSES.includes(o.status)) s.totalSpend += Number(o.amount) || 0;
    });
    return map;
  }, [ledger, orders]);

  const customers = users.filter((u) => u.role !== 'admin');

  // Suspected duplicate registered users — clustered by matching name,
  // delivery postcode, or Gmail-style email local-part using union-find so
  // transitive matches (A shares a name with B, B shares a postcode with C)
  // collapse into one group.
  const duplicateGroups = useMemo(() => {
    const parent = {};
    const find = (x) => { if (parent[x] === undefined) parent[x] = x; while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
    const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

    const byName = {}, byPostcode = {}, byEmailLocal = {};
    for (const u of customers) {
      const name = normName([u.first_name, u.last_name].filter(Boolean).join(' ')) || normName(u.full_name);
      const pc = normPostcode(u.delivery_postcode);
      const el = emailLocalPart(u.email);
      if (name && name.length >= 4) {
        if (byName[name]) union(byName[name], u.id); else byName[name] = u.id;
      }
      if (pc && pc.length >= 5) {
        if (byPostcode[pc]) union(byPostcode[pc], u.id); else byPostcode[pc] = u.id;
      }
      if (el) {
        if (byEmailLocal[el]) union(byEmailLocal[el], u.id); else byEmailLocal[el] = u.id;
      }
    }

    const buckets = {};
    for (const u of customers) {
      if (parent[u.id] === undefined) continue; // not in any signal
      const r = find(u.id);
      (buckets[r] = buckets[r] || []).push(u);
    }
    const groups = Object.values(buckets).filter((g) => g.length > 1);
    // Hide groups the admin has chosen to keep as separate accounts.
    return groups.filter((g) => !groupPairKeys(g).every((k) => dismissedKeys.has(k)));
  }, [customers, dismissedKeys]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = customers.filter((u) => {
      const name = [u.first_name, u.last_name, u.full_name].filter(Boolean).join(' ').toLowerCase();
      const matchesText = !q || name.includes(q) || u.email?.toLowerCase().includes(q);
      const matchesTag = !tagFilter || (u.tags || []).includes(tagFilter);
      const stats = statsByEmail[u.email] || { lifetimeEarned: 0, pointsBalance: 0, orderCount: 0, totalSpend: 0 };
      const matchesTier = !tierFilter || getTierForPoints(stats.lifetimeEarned).id === tierFilter;
      const matchesActivity =
        !activityFilter ||
        (activityFilter === 'no_orders' ? stats.orderCount === 0 : stats.orderCount > 0);
      return matchesText && matchesTag && matchesTier && matchesActivity;
    });

    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const sa = statsByEmail[a.email] || { lifetimeEarned: 0, pointsBalance: 0, totalSpend: 0 };
      const sb = statsByEmail[b.email] || { lifetimeEarned: 0, pointsBalance: 0, totalSpend: 0 };
      let cmp = 0;
      switch (sort.field) {
        case 'name':
          cmp = ([a.first_name, a.last_name].filter(Boolean).join(' ') || a.full_name || '')
            .localeCompare([b.first_name, b.last_name].filter(Boolean).join(' ') || b.full_name || '');
          break;
        case 'tier':
          cmp = sa.lifetimeEarned - sb.lifetimeEarned;
          break;
        case 'spend':
          cmp = sa.totalSpend - sb.totalSpend;
          break;
        case 'points':
          cmp = sa.pointsBalance - sb.pointsBalance;
          break;
        case 'joined':
          cmp = new Date(a.created_date || 0).getTime() - new Date(b.created_date || 0).getTime();
          break;
        default:
          cmp = 0;
      }
      return cmp * dir;
    });
    return list;
  }, [customers, search, tagFilter, tierFilter, activityFilter, sort, statsByEmail]);

  const ignoreGroup = async (g, idx) => {
    setIgnoring(idx);
    try {
      const keys = groupPairKeys(g);
      const records = keys.map((k) => {
        const [a, b] = k.split('|');
        return {
          user_id_a: a,
          user_id_b: b,
          pair_key: k,
          customer_names: g.map((u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email).join(' · '),
          reason: 'kept_separate',
        };
      });
      await base44.entities.DuplicateDismissal.bulkCreate(records);
      queryClient.invalidateQueries({ queryKey: ['duplicate-dismissals'] });
      toast({ title: 'Kept separate', description: 'These accounts will no longer be flagged as duplicates.' });
    } catch (e) {
      toast({ title: 'Could not ignore', description: e.message, variant: 'destructive' });
    }
    setIgnoring(null);
  };

  const undoDismissal = async (d) => {
    setUndoing(d.id);
    try {
      await base44.entities.DuplicateDismissal.delete(d.id);
      queryClient.invalidateQueries({ queryKey: ['duplicate-dismissals'] });
      toast({ title: 'Dismissal removed', description: 'These accounts may be flagged as duplicates again.' });
    } catch (e) {
      toast({ title: 'Could not undo', description: e.message, variant: 'destructive' });
    }
    setUndoing(null);
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const toggleSort = (field) => {
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }));
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0d0d0f] pt-16'}>
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSave={(updatedData) => {
            setSelectedUser((prev) => ({ ...prev, ...updatedData }));
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}

      {/* Header — the CRM page supplies its own page header when embedded. */}
      {!embedded && (
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">ORDERS &amp; CUSTOMERS</p>
            <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">CUSTOMER ACCOUNTS</h1>
            <p className="text-white/30 text-sm mt-1">{customers.length} registered customer{customers.length !== 1 ? 's' : ''} · {filtered.length} shown</p>
            <p className="text-white/30 text-xs mt-2">
              Logged in as: <span className="text-white font-medium">{currentUser.first_name || currentUser.full_name?.split(' ')[0]} ({currentUser.email})</span>
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Customer directory */}
      <section className="px-6 lg:px-10 pt-10 pb-10 max-w-6xl mx-auto">
        <div className="mb-4">
          <p className="text-primary text-xs font-bold tracking-widest mb-1">CUSTOMER ACCOUNTS</p>
          <h2 className="text-white font-black text-xl uppercase">CUSTOMER DIRECTORY</h2>
          <p className="text-white/30 text-xs mt-1">View and edit customer accounts, including their purchases, bookings and loyalty tier.</p>
        </div>

        {/* Duplicate review — tabs for possible duplicates and dismissed pairs */}
        {(duplicateGroups.length > 0 || dismissals.length > 0) && (
          <div className="mb-6 border border-white/10 bg-[#111115] p-4">
            <div className="flex items-center gap-1 mb-4 border-b border-white/5">
              <button
                onClick={() => setDupTab('duplicates')}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold tracking-widest border-b-2 transition-colors ${dupTab === 'duplicates' ? 'border-amber-400 text-amber-400' : 'border-transparent text-white/40 hover:text-white'}`}
              >
                <GitMerge className="w-3.5 h-3.5" /> POSSIBLE DUPLICATES{duplicateGroups.length > 0 ? ` (${duplicateGroups.length})` : ''}
              </button>
              <button
                onClick={() => setDupTab('dismissed')}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold tracking-widest border-b-2 transition-colors ${dupTab === 'dismissed' ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white'}`}
              >
                <UserX className="w-3.5 h-3.5" /> DISMISSED PAIRS{dismissals.length > 0 ? ` (${dismissals.length})` : ''}
              </button>
            </div>

            {dupTab === 'duplicates' ? (
              duplicateGroups.length === 0 ? (
                <p className="text-white/30 text-xs py-6 text-center">No possible duplicates detected.</p>
              ) : (
                <React.Fragment>
                  <p className="text-white/40 text-xs mb-3">Customers sharing a name, delivery postcode, or Gmail-style email alias. Review and merge their history into one account.</p>
                  <div className="space-y-2">
                    {duplicateGroups.map((g, i) => (
                      <div key={i} className="flex items-center gap-3 flex-wrap border border-white/10 bg-[#0d0d0f] px-3 py-2">
                        <span className="text-amber-400/80 border border-amber-400/30 text-[9px] font-bold tracking-widest px-1.5 py-0.5 shrink-0">{groupReason(g)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold">{g[0].first_name} {g[0].last_name}</p>
                          <p className="text-white/40 text-xs">{g.map((u) => u.email).join(' · ')}</p>
                        </div>
                        <button
                          onClick={() => { setMergePrefill({ primary: g[0].email, secondary: g[1].email }); setShowMerge(true); }}
                          className="inline-flex items-center gap-1.5 border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400 hover:text-black text-amber-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                        >
                          <GitMerge className="w-3 h-3" /> REVIEW & MERGE
                        </button>
                        <button
                          onClick={() => ignoreGroup(g, i)}
                          disabled={ignoring === i}
                          title="These are separate people — stop flagging them as duplicates"
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-white/40 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                        >
                          {ignoring === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3" />} KEEP SEPARATE
                        </button>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              )
            ) : (
              dismissals.length === 0 ? (
                <p className="text-white/30 text-xs py-6 text-center">No dismissed pairs yet.</p>
              ) : (
                <React.Fragment>
                  <p className="text-white/30 text-xs mb-3">Accounts you've marked as genuinely separate. Undo to re-flag them as potential duplicates.</p>
                  <div className="space-y-2">
                    {dismissals.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 flex-wrap border border-white/10 bg-[#0d0d0f] px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white/70 text-sm font-medium">{d.customer_names || `${d.user_id_a} · ${d.user_id_b}`}</p>
                          <p className="text-white/30 text-[10px]">Kept separate{d.created_date ? ` · ${new Date(d.created_date).toLocaleDateString('en-GB')}` : ''}</p>
                        </div>
                        <button
                          onClick={() => undoDismissal(d)}
                          disabled={undoing === d.id}
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-white/40 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                        >
                          {undoing === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} UNDO
                        </button>
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              )
            )}
          </div>
        )}

        {/* Tag definitions — managed where the accounts are listed. */}
        <div className="mb-6">
          <CustomerTagManager />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <div className="relative max-w-xs">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full appearance-none bg-[#111115] border border-white/10 text-white pl-9 pr-8 h-11 text-sm focus:border-primary/60 outline-none"
            >
              <option value="">All tags</option>
              {tags.map((t) => (
                <option key={t.id} value={t.key} className="bg-[#111115]">{t.label}</option>
              ))}
            </select>
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="appearance-none bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none"
          >
            <option value="">All tiers</option>
            {LOYALTY_TIERS.map((t) => (
              <option key={t.id} value={t.id} className="bg-[#111115]">{t.name}</option>
            ))}
          </select>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="appearance-none bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none"
          >
            <option value="">All customers</option>
            <option value="has_orders">Has orders</option>
            <option value="no_orders">No orders yet</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Users className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No customers found</p>
          </div>
        ) : (
          <div className="border border-white/5 overflow-hidden">
            {/* Sortable header */}
            <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5 items-center">
              {SORT_COLS.map((c) => (
                <button
                  key={c.key}
                  onClick={c.nosort ? undefined : () => toggleSort(c.key)}
                  className={`flex items-center gap-1 text-white/30 text-[10px] font-bold tracking-widest ${c.span} ${c.nosort ? 'cursor-default' : 'hover:text-white'}`}
                >
                  {c.label}
                  {!c.nosort && sort.field === c.key && (
                    sort.dir === 'asc' ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
                  )}
                </button>
              ))}
            </div>
            {filtered.map((u) => (
              <CustomerDirectoryRow
                key={u.id}
                user={u}
                stats={{ ...(statsByEmail[u.email] || { lifetimeEarned: 0, pointsBalance: 0, orderCount: 0, totalSpend: 0 }), tier: getTierForPoints((statsByEmail[u.email] || {}).lifetimeEarned || 0) }}
                tags={tags}
                onOpen={setSelectedUser}
              />
            ))}
          </div>
        )}
      </section>

      {/* Customer Follow-Up Email */}
      <section className="px-6 lg:px-10 pb-10 max-w-6xl mx-auto">
        <div className="mb-4">
          <p className="text-primary text-xs font-bold tracking-widest mb-1">CUSTOMER COMMUNICATION</p>
          <h2 className="text-white font-black text-xl uppercase">FOLLOW-UP EMAIL</h2>
          <p className="text-white/30 text-xs mt-1">Send the order issue explanation email to a customer and link it to a specific order.</p>
        </div>
        <CustomerFollowUpPanel customers={customers} />
      </section>

      {/* Merge Accounts */}
      <section className="px-6 lg:px-10 pb-16 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4">
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-1">DUPLICATE PROFILES</p>
            <h2 className="text-white font-black text-xl uppercase">MERGE ACCOUNTS</h2>
            <p className="text-white/30 text-xs mt-1 max-w-xl">Consolidate a customer's orders, bookings and history from a secondary email (e.g. an eBay alias) into their primary email.</p>
          </div>
          <button
            onClick={() => setShowMerge(true)}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-3 transition-colors shrink-0"
          >
            <GitMerge className="w-4 h-4" /> MERGE ACCOUNTS
          </button>
        </div>
      </section>

      {showMerge && <MergeAccountsModal customers={customers} prefill={mergePrefill} onClose={() => { setShowMerge(false); setMergePrefill(null); }} />}
    </div>
  );
}